import { Link } from 'react-router-dom';

import { PublicLayout } from './public/PublicLayout';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

export interface StaticPageSection {
  title: string;
  description?: string;
  /** Parágrafos adicionais — útil em conteúdo longo (páginas legais). */
  paragraphs?: string[];
  items?: string[];
  accent?: 'primary';
}

export interface StaticPageCta {
  primaryLabel: string;
  primaryTo: string;
  secondaryLabel?: string;
  secondaryTo?: string;
}

interface StaticPageProps {
  title: string;
  subtitle: string;
  eyebrow?: string;
  sections: StaticPageSection[];
  cta?: StaticPageCta;
  /**
   * 'cards' (default) — grade de cards, ideal para páginas institucionais curtas.
   * 'prose' — coluna única long-form, ideal para páginas legais (Termos, LGPD…).
   */
  layout?: 'cards' | 'prose';
  /** Exibido no topo das páginas legais (ex.: "Última atualização: 28/05/2026"). */
  lastUpdated?: string;
}

function StaticPage({ title, subtitle, eyebrow, sections, cta, layout = 'cards', lastUpdated }: StaticPageProps) {
  return (
    <PublicLayout>
      {/* Hero Section */}
      <div className="bg-background pb-12 pt-24 md:pb-20 md:pt-32">
        <div className="container mx-auto max-w-screen-lg px-4">
          {eyebrow && (
            <span className="text-xs font-medium uppercase tracking-[0.15em] text-primary">
              {eyebrow}
            </span>
          )}
          <h1 className="mb-4 mt-1 text-3xl font-bold text-foreground">{title}</h1>
          <p className="max-w-[640px] text-base text-muted-foreground">{subtitle}</p>
          {lastUpdated && <p className="mt-4 text-xs text-muted-foreground">{lastUpdated}</p>}
        </div>
      </div>

      {/* Content Sections */}
      {layout === 'prose' ? (
        <div className="container mx-auto max-w-screen-md px-4 pb-12 md:pb-20">
          <div className="flex flex-col gap-10">
            {sections.map((section) => (
              <section key={section.title}>
                <h2 className="mb-3 text-xl font-semibold text-foreground">{section.title}</h2>
                {section.description && (
                  <p className="text-sm leading-relaxed text-muted-foreground">{section.description}</p>
                )}
                {section.paragraphs?.map((paragraph, idx) => (
                  <p key={idx} className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {paragraph}
                  </p>
                ))}
                {section.items && (
                  <ul className="mt-3 flex list-disc flex-col gap-2 pl-5">
                    {section.items.map((item) => (
                      <li key={item} className="text-sm leading-relaxed text-muted-foreground">
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>
        </div>
      ) : (
        <div className="container mx-auto max-w-screen-lg px-4 pb-12 md:pb-20">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {sections.map((section) => (
              <Card
                key={section.title}
                className={
                  section.accent === 'primary'
                    ? 'h-full border-none bg-primary text-white'
                    : 'h-full border border-border bg-card'
                }
              >
                <CardContent className="p-6">
                  <h2
                    className={`mb-2 text-base font-semibold ${
                      section.accent === 'primary' ? 'text-white' : 'text-card-foreground'
                    }`}
                  >
                    {section.title}
                  </h2>
                  {section.description && (
                    <p className={`text-sm ${section.accent === 'primary' ? 'text-white/85' : 'text-muted-foreground'}`}>
                      {section.description}
                    </p>
                  )}
                  {section.paragraphs?.map((paragraph, idx) => (
                    <p
                      key={idx}
                      className={`mt-3 text-sm ${section.accent === 'primary' ? 'text-white/85' : 'text-muted-foreground'}`}
                    >
                      {paragraph}
                    </p>
                  ))}
                  {section.items && (
                    <div className="mt-4 flex flex-col gap-2">
                      {section.items.map((item) => (
                        <p
                          key={item}
                          className={`text-sm ${section.accent === 'primary' ? 'text-white/90' : 'text-muted-foreground'}`}
                        >
                          • {item}
                        </p>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* CTA Section */}
      {cta && (
        <div className="border-t border-border bg-muted py-12 md:py-16">
          <div className="container mx-auto max-w-screen-lg px-4">
            <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
              <div>
                <h2 className="mb-2 text-lg font-semibold text-foreground">Precisa de ajuda para avançar?</h2>
                <p className="text-sm text-muted-foreground">
                  Nossa equipe responde rapidamente e indica o melhor caminho para seu escritório.
                </p>
              </div>
              <div className="flex w-full flex-col gap-4 sm:w-auto sm:flex-row">
                <Button
                  variant="unstyled"
                  asChild
                  className="bg-gradient-to-br from-primary to-[var(--color-primary-dark)] px-7 text-white shadow hover:opacity-90"
                >
                  <Link to={cta.primaryTo}>{cta.primaryLabel}</Link>
                </Button>
                {cta.secondaryLabel && cta.secondaryTo && (
                  <Button variant="outline" asChild className="px-7 hover:bg-primary/10 hover:text-primary">
                    <Link to={cta.secondaryTo}>{cta.secondaryLabel}</Link>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </PublicLayout>
  );
}

export default StaticPage;
